import type { FinanceTx, FinanceBudget, FinancePeriod, Granularity } from "@/hooks/useFinance";
import { localISODate, dateInputFromTimestamp } from "./input";
export const currencyOf = (currency?: string | null) => (currency || "BRL").toUpperCase();
export const isIncomeTx = (tx: Pick<FinanceTx, "type">) => tx.type === "income";
export const moneySum = (values: number[]) => values.reduce((sum, value) => sum + Math.round(value * 100), 0) / 100;
export function periodStart(date: Date, granularity: Granularity) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
  if (granularity === "weekly") d.setDate(d.getDate() - (d.getDay() + 6) % 7);
  if (granularity === "monthly" || granularity === "yearly") d.setDate(1);
  if (granularity === "yearly") d.setMonth(0);
  return d;
}
/** Continuous civil periods in one currency, including empty gaps and this period. */
export function financePeriods(transactions: FinanceTx[], granularity: Granularity, currency = "BRL", count = 13, now = new Date()): FinancePeriod[] {
  const relevant = transactions.filter(tx => currencyOf(tx.currency) === currencyOf(currency));
  if (!relevant.length) return [];
  const cursor = periodStart(now, granularity), buckets = new Map<string, { income: number[]; expense: number[]; tx_count: number }>();
  for (let i = 0; i < count; i++) {
    buckets.set(localISODate(cursor), { income: [], expense: [], tx_count: 0 });
    if (granularity === "yearly") cursor.setFullYear(cursor.getFullYear() - 1);
    else if (granularity === "monthly") cursor.setMonth(cursor.getMonth() - 1);
    else cursor.setDate(cursor.getDate() - (granularity === "weekly" ? 7 : 1));
  }
  for (const tx of relevant) {
    const date = new Date(tx.occurred_at || tx.created_at); if (!Number.isFinite(date.getTime())) continue;
    const bucket = buckets.get(localISODate(periodStart(date, granularity))); if (!bucket) continue;
    bucket[isIncomeTx(tx) ? "income" : "expense"].push(Math.abs(tx.amount)); bucket.tx_count++;
  }
  return [...buckets].map(([period, bucket]) => {
    const income = moneySum(bucket.income), expense = moneySum(bucket.expense);
    return { period, income, expense, net: moneySum([income, -expense]), tx_count: bucket.tx_count };
  });
}
/** Variable budgets include one-off and legacy unclassified expenses. */
export function budgetSpend(budget: FinanceBudget, transactions: FinanceTx[], now = new Date()) {
  const month = localISODate(now).slice(0, 7), start = budget.starts_on ?? month + "-01";
  if (!budget.active || start.slice(0, 7) > month) return null;
  return moneySum(transactions.filter(tx => {
    const date = dateInputFromTimestamp(tx.occurred_at || tx.created_at);
    return !isIncomeTx(tx) && currencyOf(tx.currency) === currencyOf(budget.currency) && tx.category === budget.category &&
      (tx.recurrence === "fixed" ? "fixed" : "variable") === budget.recurrence_group && date.slice(0, 7) === month && date >= start;
  }).map(tx => Math.abs(tx.amount)));
}
export function newFinanceId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16)); bytes[6] = bytes[6] & 15 | 64; bytes[8] = bytes[8] & 63 | 128;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
